// OpenAI Proxy Utility - Routes all OpenAI calls through secure backend
// This replaces direct OpenAI client usage to prevent API key exposure

export const createOpenAIProxy = () => {
  return {
    chat: {
      completions: {
        create: async (params) => {
          console.log('🔐 Using OpenAI Proxy:', params.model, params.stream ? '(streaming)' : '(non-streaming)');
          
          if (params.stream) {
            // Handle streaming requests
            const response = await fetch('/api/openai/chat/stream', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                model: params.model,
                messages: params.messages,
                temperature: params.temperature || 0.7,
                top_p: params.top_p || 1.0,
                presence_penalty: params.presence_penalty || 0,
                frequency_penalty: params.frequency_penalty || 0,
                stream: true
              })
            });
            
            if (!response.ok) {
              const errorText = await response.text();
              console.error('❌ OpenAI Proxy streaming error:', response.status, errorText);
              throw new Error(`OpenAI Proxy Error: ${response.status} - ${errorText}`);
            }
            
            // Return async generator for streaming compatibility with existing code
            return {
              async *[Symbol.asyncIterator]() {
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                
                try {
                  while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    
                    // Process complete lines, keep incomplete line in buffer
                    buffer = lines.pop() || '';
                    
                    for (const line of lines) {
                      const trimmed = line.trim();
                      if (trimmed.startsWith('data: ')) {
                        const data = trimmed.slice(6);
                        if (data === '[DONE]') {
                          return;
                        }
                        
                        try {
                          const parsed = JSON.parse(data);
                          if (parsed.error) {
                            throw new Error(parsed.error);
                          }
                          yield parsed;
                        } catch (parseError) {
                          console.warn('⚠️ Failed to parse streaming chunk:', data);
                        }
                      }
                    }
                  }
                } finally {
                  reader.releaseLock();
                }
              }
            };
          } else {
            // Handle non-streaming requests
            const response = await fetch('/api/openai/chat', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                model: params.model,
                messages: params.messages,
                temperature: params.temperature || 0.7,
                max_tokens: params.max_tokens || 2000,
                response_format: params.response_format,
                top_p: params.top_p || 1.0,
                presence_penalty: params.presence_penalty || 0,
                frequency_penalty: params.frequency_penalty || 0
              })
            });
            
            if (!response.ok) {
              const errorText = await response.text();
              console.error('❌ OpenAI Proxy error:', response.status, errorText);
              throw new Error(`OpenAI Proxy Error: ${response.status} - ${errorText}`);
            }
            
            const completion = await response.json();
            console.log('✅ OpenAI Proxy response received:', completion.choices?.[0]?.message?.content?.length || 0, 'characters');
            return completion;
          }
        }
      }
    }
  };
};

// Helper function to check if backend proxy is available
export const checkOpenAIProxyHealth = async () => {
  try {
    const response = await fetch('/api/health');
    if (response.ok) {
      const health = await response.json();
      return health.openai_configured;
    }
    return false;
  } catch (error) {
    console.error('❌ Backend health check failed:', error);
    return false;
  }
};

// Default export for easy importing
export default createOpenAIProxy;
